import { UsageService } from '../../../core/usage/usage.service';
import { PatientService } from './../../../core/services/patient.service';
import { SpeechRecognitionService } from './../../../core/services/speech-recognition.service';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { ActivatedRoute, NavigationEnd, NavigationSkipped, Router } from '@angular/router';
import * as _ from 'lodash';
import { Subscription, take } from 'rxjs';
import Fuse, { Expression } from 'fuse.js';
import { isStateModeGroup } from 'src/app/shared/util/state-mode.util';
import { GroupService } from 'src/app/core/services/group.service';

@Component({
  selector: 'app-patient-container',
  templateUrl: './patient-container.component.html',
  styleUrls: ['./patient-container.component.scss']
})
export class PatientContainerComponent implements OnInit, OnDestroy {
  /** Marks where this work starts or finishes, for REMOAT's usage report. */
  private readonly usage = inject(UsageService);
  private readonly groupService = inject(GroupService);
  isMobile!: boolean;
  showList: boolean = true;
  showPatient: boolean = false;
  hideRight: boolean = false;
  
  /**
   * Configuration variable to control full-screen behavior
   * When true: Main route takes full screen by default, shrinks when patient outlet is loaded
   * When false: Traditional split view (40% main content, 60% patient content)
   */
  enableFullScreenMode: boolean = true;

  private subscriptions: Subscription[] = [];
  private allPatients: any[] = [];


  constructor(
    private breakpointObserver: BreakpointObserver,
    private router: Router,
    private activatedRoute: ActivatedRoute,
    private patientService: PatientService,
    private speechRecognitionService: SpeechRecognitionService,
  ) {
  }

  ngOnInit() {
    this.breakpointObserver.observe([Breakpoints.HandsetPortrait, Breakpoints.HandsetLandscape, Breakpoints.TabletPortrait])
      .subscribe(result => {
        this.isMobile = result.matches;
        this.updateLayout();
      });

    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd || event instanceof NavigationSkipped) {
        this.updateLayout();
      }
    });

    this.subscriptions.push(
      this.patientService.patients$
        .subscribe(patients => {
          this.allPatients = patients || [];
        })
    );

    // Subscribe to detected commands from speech recognition
    this.subscriptions.push(
      this.speechRecognitionService.getDetectedCommand().subscribe((commandData) => {
        this.handleVoiceCommand(commandData.command, commandData.payload);
      })
    );
  }

  ngOnDestroy(): void {
    // Unsubscribe from all subscriptions
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private handleVoiceCommand(command: string, payload?: any): void {
    switch (command) {
      case 'LOAD_PATIENT':
        console.log('Loading patient:', payload);
        // Define options for fuzzy searching
        const options = {
          keys: [
            'icdCodes.code',
            'icdCodes.description',
            'name',
            'surname',
            'patientNumber',
            'diagnosis'
          ],
          threshold: 0.3, // Adjust to control fuzziness; lower is stricter
          includeScore: true,
          shouldSort: true
        };

        // Prepare a Fuse instance with all patients and options
        const fuse = new Fuse(this.allPatients, options);

        // Filter terms
        const filterTerms = payload.toLowerCase().split(' ');
        const results = filterTerms.flatMap((term: string | Expression) => fuse.search(term));

        // Extract the top match if available
        const patient = results.length > 0 ? results[0].item : null;
        if (patient) {
          this.navigateToPatientFile(patient.id);
        } else {
          //this.showToastMessage('Unable to find the patient file to open, please try again.')
        }

        break;
      case 'ADD_INVITE':
        // A state unit invites nobody (tasks/state-mode.md).
        if (isStateModeGroup(this.groupService.group)) break;
        console.log('Opening add invite page...');
        this.addPatient('invite');
        break;
      case 'ADD_INPATIENT':
        console.log('Opening add inpatient page...');
        this.addPatient('inpatient');
        break;
      case 'ADD_OPD':
        console.log('Opening add opd page...');
        this.addPatient('opd');
        break;
      /* default:
        console.warn('Unknown command:', command); */
    }
  }

  private navigateToPatientFile(patientId: string) {
    this.patientService.getPatientById(patientId)
      .pipe(take(1))
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['file', patientId],
          }
        }]);
      }, error => {
        console.error(error);
        //this.showToastMessage('There was an error loading the patient file. Please try again.');
      });
  }

  addPatient(type?: string) {
    this.usage.flow('new patient', 'start');

    this.patientService.getPatientById(null)
      .pipe(take(1))
      .subscribe(result => {
        this.router.navigate(['/patients', {
          outlets: {
            patient: ['add'],
          }
        }], { queryParams: { type: type } });
      }, error => {
        console.error(error);
        //this.showToastMessage('There was an error creating the patient. Please try again.')
      });
  }

  private updateLayout() {
    const isPatientRoute = this.isPatientOutletActive();

    if (this.isMobile) {
      this.showList = !isPatientRoute;
      this.showPatient = isPatientRoute;
      this.hideRight = !isPatientRoute;
    } else {
      // Desktop layout logic
      if (this.enableFullScreenMode) {
        // Full-screen mode: main content takes full width when no patient outlet is active
        this.showList = true;
        this.showPatient = isPatientRoute;
        this.hideRight = !isPatientRoute;
      } else {
        // Traditional split view mode
        this.showList = true;
        this.showPatient = true;
        this.hideRight = false;
      }
    }
  }

  isPatientOutletActive(): boolean {
    let isActive = false;

    const checkOutlet = (route: ActivatedRoute): void => {
      if (route.outlet === 'patient') {
        isActive = true;
      }
      route.children.forEach(child => checkOutlet(child));
    };

    checkOutlet(this.activatedRoute.root);
    return isActive;
  }

  // Method to toggle full-screen mode (useful for testing)
  toggleFullScreenMode(): void {
    this.enableFullScreenMode = !this.enableFullScreenMode;
    this.updateLayout();
  }
}